import type { QueryClient } from '@tanstack/react-query';

import type { ProductHit } from '@libs/algolia';
import { getProductsByIds } from '@libs/api-proxy-client';

export const CART_PRODUCTS_BY_IDS_STALE_MS = 10 /* min */ * 60 /* sec */ * 1000 /* ms */;

export const CART_PRODUCTS_BY_IDS_QUERY_KEY = 'cart-products-by-ids' as const;

function normalizeNumericSkuKey(value: unknown): number | undefined {
    if (value == null) {
        return undefined;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') {
            return undefined;
        }
        const n = Number(trimmed);
        return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
}

/**
 * Numeric key for an Algolia product hit — must stay aligned with {@link lineItemSkuToProductMapKey} so cart/order
 * line `sku` strings resolve to the same {@link buildCartProductSkuKeyMap} entry.
 */
export function getProductSkuKey(product: ProductHit): number | undefined {
    return (
        normalizeNumericSkuKey(product.sku) ??
        (typeof product.id === 'number' && Number.isFinite(product.id) ? product.id : undefined) ??
        normalizeNumericSkuKey(product.objectID)
    );
}

/** Same numeric key as {@link getProductSkuKey} for a cart or order line-item `sku` string. */
export function lineItemSkuToProductMapKey(sku: string): number | undefined {
    return normalizeNumericSkuKey(sku);
}

export function buildCartProductSkuKeyMap(products: ProductHit[]): Map<number | undefined, ProductHit> {
    const productMap = new Map<number | undefined, ProductHit>();
    for (const product of products) {
        const key = getProductSkuKey(product);
        if (typeof key === 'number' && Number.isFinite(key)) {
            productMap.set(key, product);
        }
    }
    return productMap;
}

export const getCartProductBySkuQueryKey = (sku: string) => [CART_PRODUCTS_BY_IDS_QUERY_KEY, sku] as const;

const collectCartProductCacheSkus = (product: ProductHit): string[] => {
    const skus = new Set<string>();
    const numericKey = getProductSkuKey(product);

    if (typeof numericKey === 'number') {
        skus.add(String(numericKey));
    }

    if (product.objectID) {
        skus.add(String(product.objectID));
    }

    if (product.sku != null) {
        skus.add(String(product.sku));
    }

    return [...skus];
};

export const seedCartProductsBySku = (queryClient: QueryClient, products: readonly ProductHit[]): void => {
    for (const product of products) {
        for (const sku of collectCartProductCacheSkus(product)) {
            queryClient.setQueryData<ProductHit>(getCartProductBySkuQueryKey(sku), product);
        }
    }
};

export async function fetchCartProductsBySkus(
    queryClient: QueryClient,
    skus: string[],
    /** Sets `x-petm-algolia-caller` on the product-lookup request for observability (omit only in tests). */
    caller?: string
): Promise<ProductHit[]> {
    if (skus.length === 0) {
        return [];
    }
    const sortedSkus = [...new Set(skus)].sort((a, b) => a.localeCompare(b));
    const products = await queryClient.fetchQuery({
        queryKey: [CART_PRODUCTS_BY_IDS_QUERY_KEY, sortedSkus],
        queryFn: () => getProductsByIds({ ids: sortedSkus, ...(caller ? { caller } : {}) }),
        staleTime: CART_PRODUCTS_BY_IDS_STALE_MS,
        gcTime: CART_PRODUCTS_BY_IDS_STALE_MS,
    });

    seedCartProductsBySku(queryClient, products ?? []);

    return products;
}
